/**
 * ERPWorkspacePage — lean shell (imports + tab routing only).
 *
 * All tab content lives in /pages/erpWorkspace/:
 *   SetupTab · ModulesTab · ComponentMapTab · ScanTab
 *   DevToolsTab · VisibilityTab · NavPreviewTab
 */
import React, { useState, useEffect } from 'react';
import {
  Database, Layers3, Code2, Cpu, Zap, Globe, Eye,
} from 'lucide-react';
import { API, authFetch } from '../lib/api';
import { useT } from '../i18n/I18nContext.jsx';

import { SetupTab }        from './erpWorkspace/SetupTab';
import { ModulesTab }      from './erpWorkspace/ModulesTab';
import { ComponentMapTab } from './erpWorkspace/ComponentMapTab';
import { ScanTab }         from './erpWorkspace/ScanTab';
import { DevToolsTab }     from './erpWorkspace/DevToolsTab';
import { VisibilityTab }   from './erpWorkspace/VisibilityTab';
import { NavPreviewTab }   from './erpWorkspace/NavPreviewTab';

import '../styles/erp-theme.css';

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [
  { id: 'setup',      label: 'Setup & Stats',     i18nKey: 'erp.tab.setup',      icon: Database   },
  { id: 'modules',    label: 'Module Manager',    i18nKey: 'erp.tab.modules',    icon: Layers3    },
  { id: 'compmap',    label: 'Component Map',     i18nKey: 'erp.tab.compmap',    icon: Code2      },
  { id: 'scan',       label: 'Scan (Local)',       i18nKey: 'erp.tab.scan',       icon: Cpu        },
  { id: 'devtools',   label: 'Dev Tools',         i18nKey: 'erp.tab.devtools',   icon: Zap        },
  { id: 'visibility', label: 'Module Visibility', i18nKey: 'erp.tab.visibility', icon: Globe      },
  { id: 'preview',    label: 'Nav Preview',       i18nKey: 'erp.tab.preview',    icon: Eye        },
];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ERPWorkspacePage() {
  const tr = useT();
  const [tab,                setTab]                = useState('setup');
  const [modules,            setModules]            = useState([]);
  const [unmatchedHighlight, setUnmatchedHighlight] = useState(null);
  const [flashIds,           setFlashIds]           = useState(null); // Set of screenIds to highlight

  useEffect(() => {
    authFetch(`${API}/erp-workspace/admin/modules`)
      .then(r => r.json())
      .then(d => Array.isArray(d) ? setModules(d) : null)
      .catch(() => {});
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Sub-tab bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 2,
        padding: '6px 16px 0',
        background: 'var(--surface-2,#f8fafc)',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0, flexWrap: 'wrap',
      }}>
        {TABS.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            data-testid={`erpws-tab-${tb.id}`}
            style={{
              padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700,
              cursor: 'pointer', border: 'none', background: 'none',
              borderBottom: tab === tb.id
                ? '2px solid var(--erp-primary,#3b82f6)'
                : '2px solid transparent',
              color: tab === tb.id ? 'var(--erp-primary,#3b82f6)' : 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', gap: 5,
              transition: 'color 0.12s',
              marginBottom: '-1px',
            }}>
            <tb.icon size={12} />{tr(tb.i18nKey, tb.label)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {tab === 'setup'      && <SetupTab />}
        {tab === 'modules'    && <ModulesTab />}
        {tab === 'compmap'    && (
          <ComponentMapTab
            modules={modules}
            unmatchedHighlight={unmatchedHighlight}
            onClearUnmatched={() => setUnmatchedHighlight(null)}
            onNavigateScan={() => setTab('scan')}
            flashIds={flashIds}
            onClearFlash={() => setFlashIds(null)}
          />
        )}
        {tab === 'scan'       && (
          <ScanTab
            onNavigate={setTab}
            onAcceptComplete={(ids) => setFlashIds(new Set(ids))}
          />
        )}
        {tab === 'devtools'   && (
          <DevToolsTab
            onNavigate={setTab}
            onUnmatched={setUnmatchedHighlight}
          />
        )}
        {tab === 'visibility' && <VisibilityTab modules={modules} />}
        {tab === 'preview'    && <NavPreviewTab />}
      </div>
    </div>
  );
}
